import { getTotalPrice } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Slider, { Settings } from 'react-slick';
import cn from 'classnames';
import MediaQuery from 'react-responsive';

import { Baggage as BaggageType } from '../../../../BaggageService/types';
import { UpgradeBaggageHeaderProps } from '../../types';
import { useTheme } from '../../../../theme';
import { MOBILE_MIN_WIDTH } from '../../../../utils';
import { Switch } from '@shared/ui';
import { getDifferentForReturnFlight, getDisabled, getIsAllServicesCanAddedToEntireFlight } from '../../store/selector';
import { differentForReturnFlight } from '../../store/actions';
import HeaderBaggageItem from './HeaderBaggageItem/HeaderBaggageItem';
import MobileBaggageSelector from './MobileBaggageSelector/MobileBaggageSelector';
import { getBaggageTab, getSelectedServices } from '../../store/selector';
import { setBaggageTab, setCurrentSlide } from '../../store/actions';
import SlickSlider from '../../../../SlickSlider';
import { MAX_VISIBLE_BAGGAGE_COLUMNS, baggageColumnsSliderSettings } from '../../const';
import HeaderEquipmentItem from './HeaderEquipmentItem/HeaderEquipmentItem';
import Money from '../../../../Money';
import { getRelatedSegments, isActualBaggage } from '../../utils';
import { Segment } from '../../../types';
import PromoMessage from '../../../AdditionalBaggage/components/PromoMessage/PromoMessage';
import { useRenderers } from '../../../../renderProps';
import { SafeHtml } from '@shared/ui';
import { ALL_SEGMENTS_KEY } from '@shared/lib/flight';

const sliderSettings: Settings = {
	slidesToShow: 6.5,
	slidesToScroll: 5,
	dots: false,
	speed: 500,
	infinite: false,
	arrows: false
};

export interface Props {
	services: BaggageType[][];
	headerProps: UpgradeBaggageHeaderProps;
	segments: Segment[];
	mobileSelectedServiceId?: string;
	setMobileServiceId?: (id: string) => void;
	switchVariant?: 'primary' | 'secondary';
}

const BaggageDialogHeader: React.FC<Props> = props => {
	const theme = useTheme('UpgradeBaggageDialog').BaggageDialogHeader;
	const { t } = useTranslation('IncludedBaggage');

	const dispatch = useDispatch();

	const selectedServices = useSelector(getSelectedServices);
	const baggageTab = useSelector(getBaggageTab);
	const disabled = useSelector(getDisabled);

	const { MobileBaggageSelector: MobileBaggageSelectorOverride = MobileBaggageSelector } = useRenderers();

	const isDifferentForReturnFlight = useSelector(getDifferentForReturnFlight);
	const availableToEntireFlight = useSelector(getIsAllServicesCanAddedToEntireFlight);

	const segmentsCount = props.segments.length;
	const isMultipleSegments = segmentsCount > 1;

	const servicesByRfics = React.useMemo(() => {
		if (!baggageTab) {
			return [];
		}

		const servicesMap = new Map(props.services.map(services => [services[0].rfics, services]));
		const servicesByRfisc = servicesMap.get(baggageTab);

		if (!servicesByRfisc?.length) {
			return [];
		}

		if (servicesByRfisc.length === 1) {
			return servicesByRfisc;
		}

		const actualServicesMap = new Map<string, BaggageType>();
		servicesByRfisc.forEach(service => {
			const allowedSegments = new Set(service.segmentIds.reduce((segments, ids) => [...segments, ...ids], []));
			const isAllSegments = allowedSegments.size === segmentsCount;

			const segmentKey = isAllSegments ? ALL_SEGMENTS_KEY : [...allowedSegments].sort().join('/');

			const key = `${service.rfics}/${segmentKey}`;
			const existService = actualServicesMap.get(key);

			if (!existService || isActualBaggage(service, existService)) {
				actualServicesMap.set(key, service);
			}
		});

		return [...actualServicesMap.values()];
	}, [props.services, segmentsCount, baggageTab]);

	const servicePrice = React.useMemo(() => {
		if (!servicesByRfics?.length) {
			return null;
		}

		return getTotalPrice(servicesByRfics, service => {
			if (isDifferentForReturnFlight) {
				return service.price;
			}

			const relatedSegmentsCount = getRelatedSegments(service.segmentIds, null).size;

			return { ...service.price, amount: service.price.amount * relatedSegmentsCount };
		});
	}, [servicesByRfics, isDifferentForReturnFlight]);

	const actualService = servicesByRfics[servicesByRfics.length - 1];

	const handlerClickSwitcher = useCallback(() => {
		dispatch(differentForReturnFlight());
	}, [dispatch]);

	const onChangeBaggageTab = useCallback(
		(type: string) => {
			dispatch(setBaggageTab(type));
		},
		[dispatch]
	);

	const handleSlideChange = useCallback(
		(_: number, nextSlide: number) => {
			dispatch(setCurrentSlide(nextSlide));
		},
		[dispatch]
	);

	const renderCategory = useCallback(
		(services: BaggageType[]) => {
			const actualService = services[0];
			const baggageRfics = actualService.rfics;
			const baggageType = actualService.type;

			const serviceIds = new Set(services.map(service => service.id));

			const isSelected = selectedServices.some(({ serviceId, count }) => serviceIds.has(serviceId) && count > 0);

			return (
				<HeaderEquipmentItem
					key={baggageRfics}
					className={theme.categories__item}
					rfics={baggageRfics}
					baggageType={baggageType}
					isActive={baggageTab === baggageRfics}
					isSelected={isSelected}
					onClick={() => onChangeBaggageTab(baggageRfics)}
				/>
			);
		},
		[selectedServices, baggageTab, onChangeBaggageTab]
	);

	const renderCategories = useCallback(() => {
		return (
			<>
				<MediaQuery minWidth={MOBILE_MIN_WIDTH + 1}>
					<div className={theme.categories}>{props.services.map(renderCategory)}</div>
				</MediaQuery>
				<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
					<Slider className={theme.categories} {...sliderSettings}>
						{props.services.map(renderCategory)}
					</Slider>
				</MediaQuery>

				<div className={theme.category__wrapper}>
					<div className={theme.category_info}>
						<span className={theme.category_title}>{actualService.name}</span>
						<SafeHtml tag="span" className={theme.category_description} html={actualService.description} />
					</div>

					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
						<Money money={servicePrice} moneyClassName={theme.category__price} />
					</MediaQuery>
				</div>
			</>
		);
	}, [baggageTab, selectedServices, props.services, isDifferentForReturnFlight]);

	const switchElement = useMemo(
		() => (
			<Switch
				classes={{ label: theme.label }}
				checked={isDifferentForReturnFlight}
				label={t('Choose for each flight')}
				onChange={handlerClickSwitcher}
				disabled={disabled}
			/>
		),
		[disabled, isDifferentForReturnFlight, handlerClickSwitcher]
	);

	const isColumnsSliderMode = !baggageTab && props.services.length > MAX_VISIBLE_BAGGAGE_COLUMNS;

	const headerItems = props.services.map(service => (
		<HeaderBaggageItem services={service} key={service[0].rfics} allSegments={!isDifferentForReturnFlight} />
	));

	return (
		<div className={cn(theme.row)}>
			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				{baggageTab && renderCategories()}

				<div
					className={cn(theme.info, {
						[theme.equipment]: baggageTab
					})}
				>
					<PromoMessage className={theme.promoMessage} />

					{props.headerProps.description && (
						<div className={theme.titleContainer}>
							<div className={theme.description}>{props.headerProps.description}</div>
						</div>
					)}

					<div className={theme.info_price}>
						<div
							className={cn(theme.price_label, {
								[theme.perFlight]: isDifferentForReturnFlight,
								[theme.price_label_withPrice]: servicePrice
							})}
						>
							{isDifferentForReturnFlight ? (
								t('Price per flight')
							) : (
								<>
									{t('Price for all flights')}
									{servicePrice && (
										<div className={theme.price_amount}>
											<Money money={servicePrice} />
										</div>
									)}
								</>
							)}
						</div>
						{isMultipleSegments && availableToEntireFlight && switchElement}
					</div>
				</div>

				{!baggageTab && (
					<div
						className={cn(theme.baggage__container, {
							[theme.baggage__container_withSlider]: isColumnsSliderMode
						})}
					>
						{isColumnsSliderMode ? (
							<SlickSlider
								className={theme.baggage__slider}
								{...baggageColumnsSliderSettings}
								arrows
								beforeChange={handleSlideChange}
							>
								{headerItems}
							</SlickSlider>
						) : (
							headerItems
						)}
					</div>
				)}
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				{baggageTab ? (
					renderCategories()
				) : (
					<MobileBaggageSelectorOverride
						services={props.services}
						selectedBaggageId={props.mobileSelectedServiceId}
						setSelectedBaggageId={props.setMobileServiceId}
						allSegments={!isDifferentForReturnFlight}
						segments={props.segments}
					/>
				)}

				<div className={theme.toggle__wrapper}>
					<div className={cn(theme.price_label, { [theme.perFlight]: isDifferentForReturnFlight })}>
						{t(isDifferentForReturnFlight ? 'Price per flight' : 'Price for all flights')}
					</div>
					{isMultipleSegments && availableToEntireFlight && switchElement}
				</div>
			</MediaQuery>
		</div>
	);
};

export default BaggageDialogHeader;
